// v0.4.47b — Inline "Country returns" section in the Review step.
//
// Sits above LOCAL SALES. Surfaces the per-country VAT-return chips
// with their headline `due / credit / balanced` summary so an operator
// can deep-link straight into the box-by-box view of each country.
//
// Hidden when no country has activity, same pattern as the Amendments
// nav badge and the per-country tabs themselves.
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth/AuthContext";
import { CountryReturnTabs } from "./CountryReturnTabs";
import { getCountryReturnSummary } from "../lib/api";
import { BlockingMessageDialog } from "./BlockingMessageDialog";

export const CountryReturnsInline = ({ calcId }) => {
  const { t } = useTranslation();
  const { admin } = useAuth() || {};
  const isAdmin = admin?.role === "ADMIN";
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!calcId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const s = await getCountryReturnSummary(calcId);
        if (!cancelled) setSummary(s);
      } catch (e) {
        if (!cancelled) {
          setError(e?.response?.data?.detail?.message
            || e?.response?.data?.detail
            || e?.message
            || null);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [calcId]);

  // v0.6.8 fix — this used to `if (!summary) return null` unconditionally,
  // which also fired on a FAILED fetch (summary never gets set, error
  // does) -- the whole "Per-country VAT return preview" section just
  // vanished with the error div (further down, only reachable once
  // summary is set) never rendering. Show the error explicitly instead.
  if (!summary) {
    return error ? (
      <BlockingMessageDialog
        open testid="country-returns-inline-fetch-error" severity="error"
        title={t("ui.country_return.inline_load_failed", { defaultValue: "Couldn't load the country return preview" })}
        onOpenChange={(o) => !o && setError(null)}
      ><p>{error}</p></BlockingMessageDialog>
    ) : null;
  }
  // Admins see the contract-mismatch sentinel; operators don't (they
  // can't action it). The block lists countries with template_summary
  // activity that DIDN'T appear in the chip bar.
  const sentinel = summary.__contract_sentinel || {};
  const chipDrift = sentinel.drift_chip_missing_for_active_country || [];
  const unsupportedActive = sentinel.drift_active_country_no_supported_form || [];
  const hasDrift = isAdmin && (chipDrift.length > 0 || unsupportedActive.length > 0);
  const hasChips = (summary.countries || []).length > 0;
  // A2-451: a country whose filing period this calculation does not fit
  // (e.g. Germany monthly, a UK quarter) has no return here, and says why.
  const skipped = summary.skipped_countries || [];
  if (!hasChips && !hasDrift && skipped.length === 0) return null;

  return (
    <section
      data-testid="country-returns-inline"
      className="mt-6 mb-6 border border-teal-200 dark:border-teal-800 rounded-2xl bg-slate-50 dark:bg-slate-950 p-5"
    >
      {hasDrift && (
        <div
          data-testid="contract-mismatch"
          className="mb-4 border border-rose-300 dark:border-rose-500/60 bg-rose-50 dark:bg-rose-950/40 rounded-lg p-3 text-xs text-rose-900 dark:text-rose-100"
        >
          <div className="font-semibold text-rose-700 dark:text-rose-300 mb-1">
            {t("ui.country_return.inline.mismatch_title", { defaultValue: "Inconsistency (admin diagnostic) — calculation has activity in countries with no return form available" })}
          </div>
          {chipDrift.length > 0 && (
            <div data-testid="contract-mismatch-chip-missing" className="tabular-nums">
              {t("ui.country_return.inline.chip_missing", { defaultValue: "Countries with activity but no return chip shown:" })}{" "}
              <span className="text-rose-50">{chipDrift.join(", ")}</span>
            </div>
          )}
          {unsupportedActive.length > 0 && (
            <div data-testid="contract-mismatch-unsupported" className="tabular-nums">
              {t("ui.country_return.inline.unsupported", { defaultValue: "Countries with no national-return form available yet:" })}{" "}
              <span className="text-rose-50">{unsupportedActive.join(", ")}</span>
            </div>
          )}
        </div>
      )}
      <div className="flex items-center justify-between mb-3 gap-4 flex-wrap">
        <div>
          <h3 className="text-lg font-bold text-slate-900 dark:text-slate-50">
            {t("ui.country_return.inline.title", { defaultValue: "Per-country VAT return preview" })}
          </h3>
          <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5 max-w-2xl">
            {t("ui.country_return.inline.intro", { defaultValue: "Click a country to open its national VAT-return form with each box filled in by the engine. Boxes that need you to enter a value (e.g. deductible input VAT) are flagged inside." })}
          </p>
        </div>
      </div>
      {hasChips && (
        <CountryReturnTabs
          calcId={calcId}
          countries={summary.countries}
          active={null}
        />
      )}
      {skipped.map((s) => (
        <p key={s.country} data-testid={`country-return-skipped-${s.country}`}
           className="text-xs text-slate-600 dark:text-slate-300 mt-2">
          {t("ui.country_return.inline.skipped", {
            country: s.country, reason: s.reason,
            defaultValue: "No {{country}} return from this calculation: {{reason}}",
          })}
        </p>
      ))}
      {error && (
        <div data-testid="country-returns-inline-error" className="text-xs text-rose-700 dark:text-rose-300 mt-2">
          {error}
        </div>
      )}
    </section>
  );
};

export default CountryReturnsInline;
